import React, { useCallback, useState, useRef, useEffect } from 'react'
import {
  Box,
  Chip,
  Divider,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import {
  Close as CloseIcon,
  Schedule as TimeIcon
} from '@mui/icons-material'
import type { ToolType } from '../../interfaces/toolData'
import type { TimeShiftConfig } from '../../interfaces/models/TimeShift'
import { parseDeltaTime, formatDeltaTime } from '../../utils/timeShiftParsing'

export interface DeviceTimeShift {
  deviceType: ToolType
  deviceIdentifier?: string
  timeOffsetMs: number
}

export interface TimeShiftPanelProps {
  /** Current time shift configuration */
  config?: TimeShiftConfig | null
  
  /** Available device types detected in the data */
  availableDevices: ToolType[]
  
  /** Whether the panel is disabled (e.g., during loading) */
  disabled?: boolean
  
  /** Callback when time shift values change */
  onTimeShiftChange?: (deviceType: ToolType, offsetMs: number, deviceIdentifier?: string) => void
  
  /** Callback when panel should be closed */
  onClose?: () => void
  
  /** Theme for the panel */
  theme?: 'light' | 'dark'
}



/**
 * Compact time synchronization panel for right sidebar
 */
export function TimeShiftPanel({
  config,
  availableDevices = [],
  disabled = false,
  onTimeShiftChange,
  onClose,
  theme: themeMode = 'dark'
}: TimeShiftPanelProps) {
  
  // Local state for input values while user is typing
  const [inputValues, setInputValues] = useState<Map<ToolType, string>>(new Map())
  
  // Debouncing logic for onTimeShiftChange calls
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pendingChangesRef = useRef<Map<ToolType, number>>(new Map())
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [])
  
  // Debounced callback to trigger actual onTimeShiftChange
  const triggerDebouncedChange = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      // Process all pending changes
      const changes = new Map(pendingChangesRef.current)
      pendingChangesRef.current.clear()
      
      changes.forEach((offsetMs, deviceType) => {
        console.log(`🔄 Debounced change: ${deviceType} → ${offsetMs}ms`)
        onTimeShiftChange?.(deviceType, offsetMs)
      })
    }, 500) // 500ms debounce delay
  }, [onTimeShiftChange])
  
  // Convert config to easier format for UI
  const deviceShifts = React.useMemo(() => {
    console.log(`🎯 TimeShiftPanel rebuilding deviceShifts from config:`, config)
    
    if (!config) return new Map<ToolType, number>()
    
    const shifts = new Map<ToolType, number>()
    config.shifts.forEach((shift, key) => {
      console.log(`🎯 Processing shift: key=${key}, deviceType=${shift.deviceType}, offsetMs=${shift.timeOffsetMs}`)
      shifts.set(shift.deviceType, shift.timeOffsetMs)
    })
    
    console.log(`🎯 Final deviceShifts:`, Array.from(shifts.entries()))
    return shifts
  }, [config])

  const handleTimeInputChange = useCallback((deviceType: ToolType, inputValue: string) => {
    // Update local input state immediately for responsive typing
    setInputValues(prev => new Map(prev).set(deviceType, inputValue))
    
    // Parse and add to pending changes for debounced processing
    const offsetMs = parseDeltaTime(inputValue)
    pendingChangesRef.current.set(deviceType, offsetMs)
    
    // Trigger debounced change
    triggerDebouncedChange()
  }, [triggerDebouncedChange])
  
  const handleInputBlur = useCallback((deviceType: ToolType, inputValue: string) => {
    console.log(`🎯 TimeShiftPanel blur for ${deviceType}:`, { inputValue })
    
    // On blur, we trigger immediate change if there are pending changes
    // This gives users immediate feedback when they tab away or click elsewhere
    if (pendingChangesRef.current.has(deviceType)) {
      // Cancel existing debounce and trigger immediate change for this device
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
      
      const offsetMs = pendingChangesRef.current.get(deviceType)!
      pendingChangesRef.current.delete(deviceType)
      
      console.log(`🎯 Immediate blur change: ${deviceType} → ${offsetMs}ms`)
      onTimeShiftChange?.(deviceType, offsetMs)
      
      // If there are other pending changes, restart the debounce timer for them
      if (pendingChangesRef.current.size > 0) {
        triggerDebouncedChange()
      }
    }
    
    // Clear local input state - let config value take over
    setInputValues(prev => {
      const newMap = new Map(prev)
      newMap.delete(deviceType)
      console.log(`🎯 Cleared local input for ${deviceType}`)
      return newMap
    })
  }, [onTimeShiftChange, triggerDebouncedChange])
  
  const handleKeyDown = useCallback((deviceType: ToolType, inputValue: string, event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleInputBlur(deviceType, inputValue)
    }
  }, [handleInputBlur])

  const hasActiveShifts = Array.from(deviceShifts.values()).some(offset => offset !== 0)
  const activeShiftCount = Array.from(deviceShifts.values()).filter(offset => offset !== 0).length

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ 
        minHeight: '48px',
        px: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: themeMode === 'dark' ? '#1a1a1a' : '#f5f5f5',
        borderBottom: 1,
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimeIcon color="primary" />
          <Typography variant="h6">
            Time Sync
          </Typography>
          {hasActiveShifts && (
            <Chip 
              label={activeShiftCount}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {availableDevices.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No device data available
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Instructions */}
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
              Enter time shift: 5s, -1m30s, 1h, 2d, 00:05:30
            </Typography>
            
            {availableDevices.map((deviceType) => {
              const currentOffset = deviceShifts.get(deviceType) || 0
              const isActive = currentOffset !== 0
              
              // Use local input value if user is typing, otherwise use formatted config value
              const localValue = inputValues.get(deviceType)
              const displayValue = localValue !== undefined ? localValue : formatDeltaTime(currentOffset)
              
              return (
                <Box key={deviceType}>
                  {/* Device name and current offset */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="medium">
                      {deviceType}
                    </Typography>
                    {isActive && (
                      <Typography variant="caption" color="primary">
                        {currentOffset > 0 ? '+' : ''}{(currentOffset / 1000).toFixed(1)}s
                      </Typography>
                    )}
                  </Box>
                  
                  {/* Time delta input */}
                  <TextField
                    size="small"
                    fullWidth
                    value={displayValue}
                    onChange={(e) => handleTimeInputChange(deviceType, e.target.value)}
                    onBlur={(e) => handleInputBlur(deviceType, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(deviceType, displayValue, e)}
                    disabled={disabled}
                    placeholder="0s"
                    helperText={isActive ? undefined : "e.g. 5s, -1m30s, 1h, 2d"}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontFamily: 'monospace',
                        textAlign: 'center'
                      }
                    }}
                  />
                </Box>
              )
            })}
            
            {hasActiveShifts && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Changes apply automatically
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
} 